import { createSelector } from 'reselect';
import { listingDict, listingRuleDict } from '../constants/GeneralConstants';

export const getLoadingState = ({ formGeneration }) => formGeneration.isLoading;

export const selectPageStructure = ({ formGeneration }) => formGeneration.pageStructure;

export const selectVisiblePageStructure = createSelector(selectPageStructure, pageStructure => {
  pageStructure.forEach(page => {
    // solve for groups
    if (!page?.children?.find(child => child.isGroup)) {
      const pageVisible = page?.children?.find(child => child?.IsVisible);
      page.IsVisible = !!(pageVisible); // eslint-disable-line no-param-reassign
    }
    if (page?.children?.find(child => child.isGroup)) {
      const pageVisible = page?.children?.find(child => child?.fields?.find(field => field?.IsVisible));
      page.IsVisible = !!(pageVisible);// eslint-disable-line no-param-reassign
    }
  });
  return pageStructure;
});

export const selectPageStructureHasRooms = createSelector(selectPageStructure, pageStructure => pageStructure?.find(section => section.sectionName === 'Rooms') !== undefined ?? false);

export const selectAllFields = ({ formGeneration }) => formGeneration.allFields;

export const selectStatusByName = ({ formGeneration }, name, repeatableType = '', repeatableId = '') => {
  if (repeatableType !== '' && repeatableId !== '') return formGeneration?.validationResult?.[repeatableType]?.[repeatableId]?.[name];
  return formGeneration?.validationResult?.[name];
};

export const selectSectionAddClicksByName = ({ formGeneration }, name) => formGeneration?.sectionAddClicks?.[name] ?? 0;

export const selectRequiredStatusByFieldName = ({ formGeneration }, fieldName) => formGeneration.validationResult?.[fieldName]?.isRequired;

// retrieves validation results
export const selectValidationResults = ({ formGeneration }) => formGeneration?.validationResult;

// Consumes UID corresponding to a room. Returns the validation results for all fields in that room.
export const selectRoomStatusById = ({ formGeneration }, id) => formGeneration?.roomValidationResult?.[id];

export const selectComboValueDict = ({ formGeneration }) => formGeneration.comboSetValueDictionary;

export const selectAllComboBoxOptions = createSelector(
  ({ formGeneration }) => formGeneration.comboBoxOptions,
  ({ listing }) => listing.selectedListing,
  ({ formGeneration }) => formGeneration.isCopyOperation,
  (comboBoxOptions, selectedListing, isCopyOperation) => {
    if (!selectedListing?.listing || isCopyOperation) return comboBoxOptions;

    // When a listing has a disabled value we need to allow the option to show for the member to remove or keep.
    return comboBoxOptions.map(comboBoxOption => {
      if (selectedListing.listing[comboBoxOption.lookupFieldName]) {
        const selectedComboBoxValue = selectedListing.listing[comboBoxOption.lookupFieldName];
        const selectedComboBoxOption = comboBoxOption.data.find(option => option.Code === selectedComboBoxValue);
        if (selectedComboBoxOption) {
          // Ensure current option is enabled
          if (!selectedComboBoxOption.Active) {
            selectedComboBoxOption.Active = true;
            selectedComboBoxOption.Description += ' (disabled)';
          }
        }
      }
      return comboBoxOption;
    });
  },
);

export const selectFormType = ({ formGeneration }) => formGeneration.formType;

export const selectFormDefinition = ({ formGeneration }) => formGeneration?.formDefinition;

export const selectFormDefinitionType = ({ formGeneration }) => formGeneration.definitionType;

export const selectListingCode = ({ formGeneration }) => formGeneration.listingType;

export const selectRoomValidationResult = ({ formGeneration }) => formGeneration.roomValidationResult;

export const selectFormDataStatus = ({ formGeneration }) => formGeneration.formDataStatus;

export const selectRoomValidationResults = createSelector(
  selectRoomValidationResult,
  roomValidationResult => {
    const dict = [];
    if (roomValidationResult) {
      Object.entries(roomValidationResult).map(([roomID]) => {
        Object.entries(roomValidationResult[roomID]).map(([key, value]) => {
          if (key && !value.isValid) {
            dict.push({ [`Rooms.[${roomID}].${value.id}`]: value.errorMessage });
          }
          return null;
        });
        return null;
      });
    }
    return dict;
  },
);

export const selectOpenHouseValidationResults = createSelector(
  selectValidationResults,
  fullValidationResults => {
    const openHouseResults = fullValidationResults?.OpenHouse;
    if (!openHouseResults) return [];
    const dict = [];
    Object.entries(openHouseResults).map(([openHouseID]) => {
      Object.entries(openHouseResults[openHouseID]).map(([key, value]) => {
        if (key && !value.isValid) {
          dict.push({ [`OpenHouse ${value.id}`]: value.errorMessage });
        }
        return null;
      });
      return null;
    });
    return dict;
  },

);

export const selectFieldLabelDictionary = createSelector(
  selectFormDefinition,
  formDefinition => {
    const dictionary = {};
    formDefinition.map(item => {
      dictionary[item?.fieldLabel] = item?.PageName;
      return null;
    });
    dictionary.Photos = 'Photos';
    dictionary.Rooms = 'Rooms';
    dictionary.Supplements = 'Supplements';
    return dictionary;
  },
);

export const selectFieldDictionary = createSelector(
  selectFormDefinition,
  formDefinition => {
    const dictionary = {};
    formDefinition.map(item => {
      dictionary[item?.FieldName] = item?.PageName;
      return null;
    });
    dictionary.Photos = 'Photos';
    dictionary.Rooms = 'Rooms';
    dictionary.Supplements = 'Supplements';
    return dictionary;
  },
);

export const getVisibleSections = createSelector(
  selectFormDefinition,
  formDefinition => {
    const visiblePageNames = formDefinition.map(field => {
      if (field?.IsVisible) return field.PageName;
      return null;
    }).filter(entry => entry);
    const visiblePageSet = [...new Set(visiblePageNames)];
    return visiblePageSet;
    // if all fields w/PageName are visible
  },
);

// sort definition first by pageSortOrder, secondly by field SortOrder
export const getSortedDefinition = createSelector(
  selectFormDefinition,
  formDefinition => {
    const sorted = formDefinition.sort((a, b) => {
      const x = (a.pageSortOrder - b.pageSortOrder);
      if (x === 0) {
        return (a.SortOrder - b.SortOrder);
      }
      return x;
    });
    return sorted;
  },
);

// consumes sorted form definition, returns (sorted) unique sections
export const getOrderedSections = createSelector(
  // input selector
  getSortedDefinition,
  // output selector
  sortedDef => {
    const flags = {};
    const unique = sortedDef.filter(item => {
      if (flags[item.PageName]) {
        return false;
      }
      if (!item.IsVisible) {
        return false;
      }
      flags[item.PageName] = true;
      return true;
    });
    return unique.map(item => ({ sectionName: item.PageName, sectionLabel: item.PageLabel }));
  },
);

// creates a dictionary mapping between section and fields
export const selectFormStructure = createSelector(
  // input selector
  getSortedDefinition,
  // output selector
  sortedDef => {
    const structure = {};
    sortedDef.map(item => {
      if (!structure[item.PageName]) {
        structure[item.PageName] = [];
      }
      structure[item.PageName] = [
        ...structure[item.PageName],
        { ...item },
      ];
      return null;
    });
    return structure;
  },
);

// Gets/sets the initial values for the create listings form
export const selectInitialValues = createSelector(
  selectFormDefinition,
  formDefinition => {
    const initialValues = {};
    formDefinition.forEach(field => {
      if (field.datatype === 'bit') {
        // set bit fields to null by default for validation
        initialValues[field.FieldName] = null;
      } else {
        initialValues[field.FieldName] = '';
      }
      return null;
    });
    return initialValues;
  },
);

// gets/sets initial touched values for validation on mount purposes
export const selectInitialTouched = createSelector(
  selectFormDefinition,
  formDefinition => {
    const initialTouched = {};

    formDefinition.forEach(field => { initialTouched[field.FieldName] = field.IsRequired; });
    return initialTouched;
  },
);

// Needs cases for other listing types
export const selectListingType = createSelector(
  selectListingCode,
  listingType => listingDict[listingType],
);

export const selectListingRule = createSelector(
  selectListingCode,
  listingType => listingRuleDict[listingType],
);

// Retrieves first page name for the change form panel headers
export const selectEditPageName = createSelector(
  selectListingCode,
  selectPageStructure,
  (listingType, pageStructure) => {
    let pageName = '';
    const pageValues = Object.values(pageStructure);
    if (pageValues.length > 0 && listingType?.includes('chg')) {
      pageName = pageValues[0].sectionName;
    }
    return pageName;
  },
);

// Selects all field objects marked as required from the validationResult section of the data store.
export const selectRequired = createSelector(
  selectValidationResults,
  validationResult => {
    const required = [];
    Object.keys(validationResult).map(fieldName => {
      const {
        id,
        isRequired,
        isVisible,
      } = validationResult[fieldName];

      if (isRequired && isVisible) (required.push(id));
      return null;
    });
    return required;
  },
);

export const selectLookupNames = createSelector(
  selectFormDefinition,
  formDefinition => {
    const lookupTableNames = {
      lookupNames: [],
    };

    formDefinition.map(field => {
      if (field.inputType === 'ComboBox' && field.comboBoxLookupName) {
        lookupTableNames.lookupNames.push(field.comboBoxLookupName);
      }
      return null;
    });
    return lookupTableNames;
  },
);

// Retrieves combo options for a condo lookup name
// Using instead of selectComboBoxOptions since that selector is mainly for listings
export const selectCondoMgmtComboBoxOptions = createSelector(
  [
    selectAllComboBoxOptions,
    (_, lookupName) => lookupName,
  ],
  (allComboBoxOptions, lookupName) => {
    const comboBoxOptions = [];
    allComboBoxOptions.forEach(optionGroups => {
      if (lookupName === optionGroups.tableName) {
        optionGroups.data.forEach(option => { // add option object to list for specific field.
          if (option.Active && option.ParentIsActive) {
            comboBoxOptions.push({
              label: option.Description,
              value: `${option.Code ? option.Code : ''}`,
              parents: option.Parents,
            });
          }
        });
      }
    });
    return comboBoxOptions;
  },
);

// Gets all of the options for the given ComboBox Field
export const selectComboBoxOptions = createSelector(
  [
    selectFormDefinition,
    selectAllComboBoxOptions,
    (_, lookupName) => lookupName,
    (_, __, lookupRequired) => lookupRequired,
  ],
  (formDefinition, allComboBoxOptions, lookupName, lookupRequired = true) => {
    if (!lookupRequired) return null;

    // if this is using the boptions from formGeneration, then you need to be able to manipulate what's available in that array.
    // lookupName == field name

    const comboBoxOptions = [];
    let comboBoxLookupName;
    // wrapped in an if so that FIGs can be used for comboBox Population in Search Listing forms
    if (formDefinition !== []) {
      // Goes through the formDefinition to get the lookupTableName based on the fieldName
      formDefinition.forEach(field => {
        if (field.FieldName === lookupName) {
          comboBoxLookupName = field.comboBoxLookupName;
        }
      });

      // If there was a comboBoxLookupName, now loop through the comboBox options to get the relevant data
      if (comboBoxLookupName) {
        allComboBoxOptions.forEach(optionGroups => {
          if (comboBoxLookupName === optionGroups.tableName) { // matches the table name to the fieldName
            optionGroups.data.forEach(option => { // add option object to list for specific field.
              // can you just add a ParentActive? prop
              if (option.Active && option.ParentIsActive) {
                comboBoxOptions.push({ // returned boptions.
                  label: option.Description,
                  value: `${option.Code ? option.Code : ''}`,
                  parents: option.Parents,
                });
              }
            });
          }
        });
      }
    }

    // Retrieves comboBoxOptions for dropdown that are not part of a listing form
    if (formDefinition.length === 0) {
      allComboBoxOptions.forEach(optionGroup => {
        if (lookupName === optionGroup.tableName) {
          optionGroup.data.forEach(option => { // add option object to list for specific field.
            if (option.Active) {
              comboBoxOptions.push({ // returned boptions.
                label: option.Description,
                value: `${option.Code ? option.Code : ''}`,
                parents: option.Parents,
              });
            }
            return null;
          });
        }
        return null;
      });
    }

    return comboBoxOptions;
  },
);

export const selectYNOptions = ({ formGeneration }, fieldname) => {
  const requiredOptions = [];
  let YNfield;

  // check that field retrieved matches the fieldname passed in and check if its a YN field
  formGeneration.formDefinition.map(field => {
    if (field.FieldName === fieldname) {
      if (field.datatype === 'bit') {
        YNfield = field.FieldName;
      }
    }
    return null;
  });

  /*
    YN field required : Yes/No/Empty
    YN field !required : Yes/No
  */
  if (YNfield) {
    formGeneration.formDefinition.map(field => {
      if (YNfield === field.FieldName) {
        requiredOptions.push(
          { label: 'Yes', value: 1 },
          { label: 'No', value: 0 },
        );
      }
      return null;
    });
  }

  return requiredOptions;
};

// Receives array of sorted fields and retrieves the error messages to display in the same order
export const getSortedErrors = createSelector(
  selectFormStructure,
  selectValidationResults,
  selectRoomValidationResults,
  selectOpenHouseValidationResults,
  (structure, validationResult, roomValidationResult, openHouseValidationResult) => {
    const orderedErrors = [];
    if (validationResult !== null) {
      const orderedSections = Object.keys(structure);
      const valKeys = Object.keys(validationResult);
      if (valKeys.length > 0) {
        orderedSections.map(section => {
          const sectionResults = structure[section].sort((a, b) => a.SortOrder - b.SortOrder);
          sectionResults.map(field => {
            if (valKeys.includes(field.FieldName)) {
              const {
                isVisible, isValid, errorMessage,
              } = validationResult[field.FieldName];

              if (isVisible && !isValid && errorMessage) {
                orderedErrors.push({ [field.fieldLabel]: errorMessage });
              }
            }
            return null;
          });
          if (section === 'Photos' && valKeys.includes('Photos') && validationResult.Photos.errorMessage) {
            orderedErrors.push({ Photos: validationResult.Photos.errorMessage });
          }
          if (section === 'Supplements' && valKeys.includes('Supplements') && validationResult.Supplements.errorMessage) {
            orderedErrors.push({ Supplements: validationResult.Supplements.errorMessage });
          }
          if (section === 'Rooms' && valKeys.includes('RoomDimensions') && validationResult.RoomDimensions.errorMessage) {
            orderedErrors.push({ 'Room Dimensions': validationResult.RoomDimensions.errorMessage });
          }
          if (section === 'Rooms' && valKeys.includes('RoomTypeMinimumRequirements') && validationResult.RoomTypeMinimumRequirements.errorMessage) {
            orderedErrors.push({ 'Room Type Minimum Requirements': validationResult.RoomTypeMinimumRequirements.errorMessage });
          }
          if (section === 'Rooms' && roomValidationResult) {
            orderedErrors.push(...roomValidationResult);
          }
          if (section === 'OpenHouses' && openHouseValidationResult) orderedErrors.push(...openHouseValidationResult);
          return null;
        });
      }
    }
    return orderedErrors;
  },
);

export const getSortedErrorsOfInvisibleFields = createSelector(
  selectFormStructure,
  selectValidationResults,
  selectRoomValidationResults,
  (structure, validationResult, roomValidationResult) => {
    const orderedErrors = [];
    if (validationResult !== null) {
      const orderedSections = Object.keys(structure);
      const valKeys = Object.keys(validationResult);
      if (valKeys.length > 0) {
        orderedSections.map(section => {
          const sectionResults = structure[section].sort((a, b) => a.SortOrder - b.SortOrder);
          sectionResults.map(field => {
            if (valKeys.includes(field.FieldName)) {
              const {
                isVisible, isValid, errorMessage,
              } = validationResult[field.FieldName];

              if (!isVisible && !isValid && errorMessage) {
                orderedErrors.push({ [field.fieldLabel]: errorMessage });
              }
            }
            return null;
          });
          if (section === 'Photos' && valKeys.includes('Photos') && validationResult.Photos.errorMessage) {
            orderedErrors.push({ Photos: validationResult.Photos.errorMessage });
          }
          if (section === 'Supplements' && valKeys.includes('Supplements') && validationResult.Supplements.errorMessage) {
            orderedErrors.push({ Supplements: validationResult.Supplements.errorMessage });
          }
          if (section === 'Rooms' && valKeys.includes('RoomDimensions') && validationResult.RoomDimensions.errorMessage) {
            orderedErrors.push({ 'Room Dimensions': validationResult.RoomDimensions.errorMessage });
          }
          if (section === 'Rooms' && valKeys.includes('RoomTypeMinimumRequirements') && validationResult.RoomTypeMinimumRequirements.errorMessage) {
            orderedErrors.push({ 'Room Type Minimum Requirements': validationResult.RoomTypeMinimumRequirements.errorMessage });
          }
          if (section === 'Rooms' && roomValidationResult) {
            orderedErrors.push(...roomValidationResult);
          }
          return null;
        });
      }
    }
    return orderedErrors;
  },
);

export const selectErrorsPerSection = createSelector(
  getSortedErrors,
  selectFieldLabelDictionary,
  selectRoomValidationResults,
  selectOpenHouseValidationResults,
  (errors, dictionary, roomValidationResult, openHouseResult) => {
    const errorsPerSection = {};
    errorsPerSection.Rooms = [];
    errorsPerSection.OpenHouses = [];
    errors.map(error => {
      const [fieldName] = Object.keys(error);
      const section = dictionary[fieldName];
      if (fieldName === 'Room Dimensions' || fieldName === 'Room Type Minimum Requirements') {
        errorsPerSection.Rooms = [...errorsPerSection.Rooms, fieldName];
      } else if (!errorsPerSection[section]) {
        errorsPerSection[section] = [fieldName];
      } else if (errorsPerSection[section] && !errorsPerSection[section].includes(fieldName)) {
        errorsPerSection[section] = [...errorsPerSection[section], fieldName];
      }
      return null;
    });
    if (roomValidationResult) {
      errorsPerSection.Rooms = [...errorsPerSection.Rooms, ...roomValidationResult];
    }
    if (!errorsPerSection.Rooms.length) {
      delete errorsPerSection.Rooms;
    }
    if (openHouseResult) {
      errorsPerSection.OpenHouses = [...errorsPerSection.OpenHouses, ...openHouseResult];
    }
    if (!errorsPerSection.OpenHouses.length) {
      delete errorsPerSection.OpenHouses;
    }
    return errorsPerSection;
  },
);

export const selectRequiredPerSection = createSelector(
  selectRequired,
  selectFieldDictionary,
  (required, dictionary) => {
    const reqPerSection = {};
    required.map(req => {
      const section = dictionary[req];

      if (!reqPerSection[section]) (reqPerSection[section] = [req]);
      if (reqPerSection[section] && !reqPerSection[section].includes(req)) {
        reqPerSection[section] = [...reqPerSection[section], req];
      }
      return null;
    });
    return reqPerSection;
  },
);

export const getFieldNameAndLabels = createSelector(
  selectFormDefinition,
  formDefinition => (
    formDefinition?.flatMap(singleField => (
      [{
        fieldName: singleField?.FieldName,
        fieldLabel: singleField?.fieldLabel,
      }]
    ))
  ),
);

export const getVisibleFieldNameAndLabels = createSelector(
  selectFormDefinition,
  formDefinition => (
    formDefinition?.flatMap(singleField => (
      (singleField?.IsVisible || singleField?.isVisible)
        ? [{
          fieldName: singleField?.FieldName,
          fieldLabel: singleField?.fieldLabel,
        }] : []
    ))
  ),
);

export const transformComboBoxData = createSelector(
  selectAllComboBoxOptions,
  allComboBoxOptions => {
    const formattedComboBoxOptions = allComboBoxOptions.flatMap(tableOptions => {
      const comboData = tableOptions.data.flatMap(singleOption => {
        if (!singleOption.Active) return [];

        return [{
          label: singleOption.Description,
          value: singleOption.Description,
          formValue: tableOptions.lookupFieldName,
        }];
      });
      if (!comboData.length) return [];

      return [{
        label: tableOptions.lookupFieldName,
        options: comboData,
      }];
    });

    return formattedComboBoxOptions;
  },
);

const transformFieldOptions = createSelector(
  getVisibleFieldNameAndLabels,
  fields => {
    const fieldOptions = fields.flatMap(fieldData => (
      [{
        label: fieldData.fieldLabel,
        value: fieldData.fieldName,
        formValue: fieldData.fieldName,
      }]
    ));
    return {
      label: 'Fields',
      options: fieldOptions,
    };
  },
);

export const selectFormSearchOptions = createSelector(
  transformComboBoxData,
  transformFieldOptions,
  (comboBoxOptionsGroup, fieldOptions) => {
    const groupedOptions = [];
    groupedOptions.push(fieldOptions);
    /*
      Currently, adding the comboBoxOptionsGroup to the options for the form search
        causes lag. The Select in FormSearchBar will need to be refactored to be async,
        and after talking with Aaron it was decided that we will leave comboBoxOptions
        out for now and will come back to it for v2
    */
    // groupedOptions = groupedOptions.concat(comboBoxOptionsGroup);

    return groupedOptions;
  },
);
